<template>
  <div class="space-y-4">
    <!-- Format Selection -->
    <div class="flex gap-2">
      <button
        v-for="formatOption in formats"
        :key="formatOption.key"
        :class="[
          'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
          selectedFormat === formatOption.key
            ? 'bg-blue-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        ]"
        @click="selectedFormat = formatOption.key">
        <fa-icon :icon="['far', formatOption.icon]" class="text-xs" />
        {{ formatOption.label }}
      </button>
    </div>

    <!-- Review Preview -->
    <div class="space-y-3">
      <!-- Container sized to image -->
      <div class="overflow-hidden relative rounded-lg">
        <!-- Loading state -->
        <div
          v-if="isLoadingPreview || isGeneratingPreview"
          class="flex absolute inset-0 justify-center items-center">
          <fa-icon icon="spinner-third" spin class="text-2xl text-gray-400" />
          <span class="ml-2 text-sm text-gray-400">Generating preview...</span>
        </div>

        <!-- Preview -->
        <img
          v-else-if="previewUrl"
          :src="previewUrl"
          :alt="`${format.label} review preview`"
          class="w-full rounded-lg"
          draggable="false" />

        <!-- No preview fallback -->
        <div
          v-else
          class="flex absolute inset-0 flex-col justify-center items-center text-gray-500">
          <fa-icon icon="image" class="mb-2 text-3xl" />
          <span class="text-xs">No preview available</span>
        </div>
      </div>
    </div>

    <!-- Download button -->
    <button
      :disabled="isDownloading || !previewUrl || isGeneratingPreview"
      class="group relative w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden shadow-lg shadow-indigo-600/30 hover:shadow-xl hover:shadow-indigo-600/40 hover:scale-[1.02] active:scale-[0.98]"
      @click="handleDownload">
      <!-- Animated background gradient -->
      <div
        class="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

      <!-- Button content -->
      <div class="flex relative z-10 gap-2 items-center">
        <fa-icon
          :icon="
            isDownloading || isGeneratingPreview
              ? 'spinner-third'
              : Capacitor.isNativePlatform()
                ? 'arrow-up-from-bracket'
                : 'download'
          "
          :spin="isDownloading || isGeneratingPreview"
          class="text-lg" />
        <span class="text-sm">
          {{
            isDownloading
              ? Capacitor.isNativePlatform()
                ? linkCopied
                  ? 'Link Copied! Sharing...'
                  : 'Sharing...'
                : 'Downloading...'
              : isGeneratingPreview
                ? 'Generating...'
                : Capacitor.isNativePlatform()
                  ? 'Share Review Card'
                  : 'Download Review Card'
          }}
        </span>
        <fa-icon
          v-if="!isDownloading && !isGeneratingPreview"
          :icon="Capacitor.isNativePlatform() ? 'share-nodes' : 'arrow-down'"
          class="text-xs opacity-75 group-hover:translate-y-0.5 transition-transform duration-300" />
      </div>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { useReviewColors } from '@/use/useReviewColors'
import { renderTextOverlay } from '@/composables/useReviewTextOverlay'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

const props = defineProps({
  review: {
    type: Object,
    required: true
  },
  title: {
    type: Object,
    default: null
  },
  imageBlobs: {
    type: Object,
    default: null
  },
  label: {
    type: String,
    default: null
  },
  showLabel: {
    type: Boolean,
    default: true
  }
})

const storeReviews = useStoreReviews()
const storeTMDB = useStoreTMDB()
const { scoreHsl } = useReviewColors()

// State
const isLoadingPreview = ref(false)
const isDownloading = ref(false)
const isGeneratingPreview = ref(false)
const linkCopied = ref(false)
const sourceImage = ref(null)
const isStoredImage = ref(false)
const previewUrl = ref(null)
const selectedFormat = ref('wide')

// Available formats
const formats = {
  wide: {
    key: 'wide',
    label: '16:9',
    icon: 'film',
    width: 1920,
    height: 1080
  },
  tall: {
    key: 'tall',
    label: '4:5',
    icon: 'mobile-screen',
    width: 1080,
    height: 1350
  }
}

const format = computed(() => formats[selectedFormat.value])

// Extract review data for text overlay
const reviewTitle = computed(() => {
  if (props.review?.title_title) return props.review.title_title
  return props.title?.title || props.title?.name || ''
})

const reviewSubtitle = computed(() => {
  if (props.review?.title_subtitle && props.review.title_subtitle.trim()) {
    return props.review.title_subtitle
  }

  // Check tmdb_cache for year data
  if (props.review?.titles?.tmdb_cache?.release_date) {
    const year = new Date(props.review.titles.tmdb_cache.release_date)
      .getFullYear()
      .toString()
    return year
  } else if (props.review?.titles?.tmdb_cache?.first_air_date) {
    const firstYear = new Date(
      props.review.titles.tmdb_cache.first_air_date
    ).getFullYear()
    if (props.review.titles.tmdb_cache?.last_air_date) {
      const lastYear = new Date(
        props.review.titles.tmdb_cache.last_air_date
      ).getFullYear()
      return firstYear === lastYear
        ? `${firstYear}`
        : `${firstYear} - ${lastYear}`
    }
    return props.review.titles.tmdb_cache.next_episode_to_air
      ? `${firstYear} - Present`
      : `${firstYear}`
  }

  // Check nested titles object
  if (props.review?.titles?.release_date) {
    const year = new Date(props.review.titles.release_date)
      .getFullYear()
      .toString()
    return year
  } else if (props.review?.titles?.first_air_date) {
    const firstYear = new Date(props.review.titles.first_air_date).getFullYear()
    if (props.review.titles?.last_air_date) {
      const lastYear = new Date(props.review.titles.last_air_date).getFullYear()
      return firstYear === lastYear
        ? `${firstYear}`
        : `${firstYear} - ${lastYear}`
    }
    return props.review.titles.next_episode_to_air
      ? `${firstYear} - Present`
      : `${firstYear}`
  }

  // Fallback to original title prop
  if (props.title?.release_date) {
    const year = new Date(props.title.release_date).getFullYear().toString()
    return year
  } else if (props.title?.first_air_date) {
    const firstYear = new Date(props.title.first_air_date).getFullYear()
    if (props.title?.last_air_date) {
      const lastYear = new Date(props.title.last_air_date).getFullYear()
      return firstYear === lastYear
        ? `${firstYear}`
        : `${firstYear} - ${lastYear}`
    }
    return props.title.next_episode_to_air
      ? `${firstYear} - Present`
      : `${firstYear}`
  }

  return null
})

const reviewUsername = computed(() => {
  return props.review?.profiles?.username || null
})

const reviewScore = computed(() => {
  if (
    props.review?.score_overall === false ||
    props.review?.score_overall === undefined
  ) {
    return null
  }
  return props.review?.score_overall
})

// Get TMDB fallback URL from review or title props
const getTmdbFallbackUrl = () => {
  if (props.review?.image_path) {
    return `https://image.tmdb.org/t/p/original${props.review.image_path}`
  } else if (props.title?.backdrop_path) {
    return `https://image.tmdb.org/t/p/original${props.title.backdrop_path}`
  } else if (props.title?.poster_path) {
    return `https://image.tmdb.org/t/p/original${props.title.poster_path}`
  }
  return null
}

// Try to load an image URL, returns the loaded Image or null on failure
const tryLoadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

// Load source image
const loadSourceImage = async () => {
  if (!props.review?.id) return

  isLoadingPreview.value = true

  try {
    let blobUrl = null
    let loadedImg = null
    let usingStoredImage = false

    // Check if we have a pre-generated blob
    if (props.imageBlobs?.plain) {
      blobUrl = URL.createObjectURL(props.imageBlobs.plain)
      loadedImg = await tryLoadImage(blobUrl)
      if (loadedImg) usingStoredImage = true
    }

    // Try stored image from API
    if (!loadedImg) {
      try {
        const storedUrl = await storeReviews.getReviewImageUrl(
          props.review.id,
          'plain'
        )
        if (storedUrl) {
          loadedImg = await tryLoadImage(storedUrl)
          if (loadedImg) usingStoredImage = true
        }
      } catch (e) {
        console.log('No stored image, trying TMDB fallback')
      }
    }

    // Fallback to TMDB image
    if (!loadedImg) {
      const tmdbUrl = getTmdbFallbackUrl()
      if (tmdbUrl) {
        loadedImg = await tryLoadImage(tmdbUrl)
      }
      usingStoredImage = false
    }

    if (loadedImg) {
      isStoredImage.value = usingStoredImage
      sourceImage.value = loadedImg
      nextTick(() => {
        generatePreview()
      })
    }

    // Clean up blob URL
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
    }
  } catch (error) {
    console.error('Failed to load source image:', error)
  } finally {
    isLoadingPreview.value = false
  }
}

// Generate preview at full resolution (same as final export)
// The <img> tag scales it down visually, giving crisp retina quality
const generatePreview = async () => {
  if (!sourceImage.value) return

  isGeneratingPreview.value = true

  try {
    // Clean up previous preview
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value)
    }

    const blob = await generateFinalImage()
    if (blob) {
      previewUrl.value = URL.createObjectURL(blob)
    }
  } catch (error) {
    console.error('Failed to generate preview:', error)
  } finally {
    isGeneratingPreview.value = false
  }
}

// Generate final 1920x1080 image
const generateFinalImage = async () => {
  if (!sourceImage.value) return null

  const width = format.value.width
  const height = format.value.height

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  // Draw black background
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, width, height)

  // Calculate image dimensions
  const canvasAspect = width / height
  const imgAspect = sourceImage.value.width / sourceImage.value.height

  let drawWidth, drawHeight, drawX, drawY

  // Cover mode: fill the canvas, may crop - centered
  if (imgAspect > canvasAspect) {
    drawHeight = height
    drawWidth = drawHeight * imgAspect
  } else {
    drawWidth = width
    drawHeight = drawWidth / imgAspect
  }

  // Center the image
  drawX = (width - drawWidth) / 2
  drawY = (height - drawHeight) / 2

  // Apply color adjustments if using TMDB image
  if (!isStoredImage.value && reviewScore.value !== null) {
    ctx.filter = 'grayscale(1) brightness(1.25)'
  }

  // Draw image
  ctx.drawImage(sourceImage.value, drawX, drawY, drawWidth, drawHeight)

  // Reset filter
  ctx.filter = 'none'

  // Apply color multiply blend if using TMDB image
  if (!isStoredImage.value && reviewScore.value !== null) {
    const scoreColorValue = scoreHsl(reviewScore.value)
    ctx.globalCompositeOperation = 'multiply'
    ctx.fillStyle = scoreColorValue
    ctx.fillRect(0, 0, width, height)
    ctx.globalCompositeOperation = 'source-over'
  }

  // Draw text overlay at full resolution
  await renderTextOverlay(ctx, {
    width,
    height,
    username: reviewUsername.value,
    title: reviewTitle.value,
    subtitle: reviewSubtitle.value,
    score: reviewScore.value,
    scale: 1,
    showLogo: true,
    showDomain: true,
    isPreview: false
  })

  // Convert to blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob)
      },
      'image/jpeg',
      0.95
    )
  })
}

// Download 16:9 image (native iOS support)
const handleDownload = async () => {
  if (!sourceImage.value || isDownloading.value) return

  isDownloading.value = true

  try {
    // Create filename
    const titleSlug = (
      props.title?.title ||
      props.title?.name ||
      props.review?.title_title ||
      'review'
    )
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')

    const usernameSlug = reviewUsername.value
      ? reviewUsername.value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
      : 'user'

    const filename = `${usernameSlug}-${titleSlug}-${format.value.label.replace(':', 'x')}.jpg`

    const blob = await generateFinalImage()
    if (blob) {
      if (Capacitor.isNativePlatform()) {
        // Native iOS/Android: Save to filesystem and share
        try {
          // Convert blob to base64 safely
          const reader = new FileReader()
          const base64String = await new Promise((resolve, reject) => {
            reader.onload = () => {
              const result = reader.result
              // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
              const base64 = result.split(',')[1]
              resolve(base64)
            }
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })

          // Write file to device
          const result = await Filesystem.writeFile({
            path: filename,
            data: base64String,
            directory: Directory.Cache
          })

          // Copy review link to clipboard for easy pasting in Instagram Stories
          if (props.review?.id) {
            const reviewUrl = `https://spectra.reviews/reviews/${props.review.id}`
            try {
              await navigator.clipboard.writeText(reviewUrl)
              linkCopied.value = true
            } catch (e) {
              console.log('Clipboard copy failed:', e)
            }
          }

          // Share the file - include URL in text so it's useful for other platforms
          // and if iOS overwrites clipboard, the URL is still in the share text
          const reviewUrl = props.review?.id
            ? `https://spectra.reviews/reviews/${props.review.id}`
            : null
          const shareText = reviewUrl
            ? `Check out my review on Spectra: ${reviewUrl}`
            : undefined

          await Share.share({
            text: shareText,
            url: result.uri,
            dialogTitle: 'Share Review Card'
          })
        } catch (error) {
          console.error('Native sharing failed:', error)
          // Fallback to web download
          fallbackWebDownload(blob, filename)
        }
      } else {
        // Web: Standard download
        fallbackWebDownload(blob, filename)
      }
    }
  } catch (error) {
    console.error('Failed to download image:', error)
  } finally {
    isDownloading.value = false
    linkCopied.value = false
  }
}

// Fallback web download function
const fallbackWebDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Watch for review changes
watch(
  () => props.review?.id,
  () => {
    loadSourceImage()
  }
)

// Watch for format changes
watch(
  () => selectedFormat.value,
  () => {
    if (sourceImage.value) {
      generatePreview()
    }
  }
)

onUnmounted(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

// Load initial source image and fetch title data
onMounted(() => {
  loadSourceImage()

  // Fetch title data if we have tmdb_id
  if (props.review?.tmdb_id && props.review?.tmdb_type) {
    storeTMDB.fetchTitleById(props.review.tmdb_id, props.review.tmdb_type)
  }
})
</script>
